import {
  chunkCenterWorld,
  latLngToWorld,
  type ChunkCoord,
  type SessionFrame,
} from '../world/coords.js';

export type MinimapMode = 'north' | 'heading';

export interface ChunkStateView {
  coord: ChunkCoord;
  status: 'active' | 'cached' | 'pending';
}

export interface OrderPinView {
  lat: number;
  lng: number;
  kind: 'pickup' | 'dropoff';
  premium: boolean;
  pulsePhase: number;
  claimedByOther: boolean;
}

export interface MinimapUpdate {
  playerX: number;
  playerZ: number;
  /** Heading in radians: 0 = +Z (north), increases clockwise toward +X (east). */
  headingRad: number;
  chunks: ChunkStateView[];
  dt: number;
  /** World-space positions of all active AI traffic cars. */
  trafficCars?: Array<{ x: number; z: number }>;
  /** Delivery-mode pickup/dropoff pins (lat/lng, projected by the minimap). */
  orderPins?: OrderPinView[];
}

/** Discrete zoom levels — meters of view radius (i.e. half the visible square's side). */
export const ZOOM_LEVELS_M: readonly number[] = [500, 1000, 1500, 3000, 6000] as const;
/** Default index — 1500 m matches the original fixed view and the chunk size. */
const DEFAULT_ZOOM_INDEX = 2;
/** Pick an OSM tile zoom level based on view radius so detail stays readable as we zoom out.
 *  Smaller view radius → higher tile zoom (more detail per pixel). */
function tileZoomFor(viewRadiusM: number): number {
  if (viewRadiusM <= 600) return 16;
  if (viewRadiusM <= 1200) return 15;
  if (viewRadiusM <= 2000) return 14;
  if (viewRadiusM <= 4000) return 13;
  return 12;
}
const TILE_URL = (z: number, x: number, y: number) =>
  `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;

export class Minimap {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private size: number;
  private frame: SessionFrame | null = null;
  private mode: MinimapMode = 'north';
  private smoothedHeading = 0;
  private tileCache = new Map<string, HTMLImageElement>();
  private onModeChange?: (mode: MinimapMode) => void;
  private zoomIndex: number = DEFAULT_ZOOM_INDEX;
  private onZoomChange?: (zoomIndex: number, viewRadiusM: number) => void;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Minimap: 2D context unavailable');
    this.ctx = ctx;
    this.size = canvas.width;
  }

  // --------- Zoom ---------

  getZoomIndex(): number {
    return this.zoomIndex;
  }

  getViewRadiusM(): number {
    return ZOOM_LEVELS_M[this.zoomIndex] ?? 1500;
  }

  canZoomIn(): boolean {
    return this.zoomIndex > 0;
  }

  canZoomOut(): boolean {
    return this.zoomIndex < ZOOM_LEVELS_M.length - 1;
  }

  setZoomIndex(idx: number): void {
    const clamped = Math.max(0, Math.min(ZOOM_LEVELS_M.length - 1, idx));
    if (clamped === this.zoomIndex) return;
    this.zoomIndex = clamped;
    // Different zoom may use a different tile zoom — drop the old cache so we re-fetch.
    this.tileCache.clear();
    this.onZoomChange?.(this.zoomIndex, this.getViewRadiusM());
  }

  zoomIn(): void {
    this.setZoomIndex(this.zoomIndex - 1);
  }

  zoomOut(): void {
    this.setZoomIndex(this.zoomIndex + 1);
  }

  setOnZoomChange(cb: (zoomIndex: number, viewRadiusM: number) => void): void {
    this.onZoomChange = cb;
  }

  setFrame(frame: SessionFrame | null): void {
    this.frame = frame;
    this.tileCache.clear();
  }

  setMode(mode: MinimapMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.onModeChange?.(mode);
  }

  toggleMode(): void {
    this.setMode(this.mode === 'north' ? 'heading' : 'north');
  }

  getMode(): MinimapMode {
    return this.mode;
  }

  setOnModeChange(cb: (mode: MinimapMode) => void): void {
    this.onModeChange = cb;
  }

  update(u: MinimapUpdate): void {
    const ctx = this.ctx;
    const size = this.size;
    const cx = size * 0.5;
    const cy = size * 0.5;
    const viewRadiusM = this.getViewRadiusM();
    const scale = size / (viewRadiusM * 2); // px per meter

    // Heading smoothing — only needed in heading-up mode but cheap to always run.
    const target = u.headingRad;
    const delta = wrapAngle(target - this.smoothedHeading);
    const k = 1 - Math.exp(-4.0 * u.dt);
    this.smoothedHeading = wrapAngle(this.smoothedHeading + delta * k);

    ctx.save();
    ctx.clearRect(0, 0, size, size);

    // Clip to a rounded square so map content doesn't overflow into the canvas corners.
    roundedRectPath(ctx, 0, 0, size, size, 6);
    ctx.clip();

    if (!this.frame) {
      ctx.fillStyle = '#1a1f25';
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = '#8a96a3';
      ctx.font = '12px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('No location loaded', cx, cy);
      ctx.restore();
      return;
    }

    // Fill background while no tiles are loaded.
    ctx.fillStyle = '#2a3138';
    ctx.fillRect(0, 0, size, size);

    // In heading-up mode, rotate the whole map so the player's forward points up.
    const rot = this.mode === 'heading' ? -this.smoothedHeading : 0;
    ctx.translate(cx, cy);
    if (rot !== 0) ctx.rotate(rot);
    ctx.translate(-cx, -cy);

    // Tiles, then chunk overlay, then traffic cars, then order pins.
    this.drawTiles(u.playerX, u.playerZ, scale, viewRadiusM);
    this.drawChunkOverlay(u.chunks, u.playerX, u.playerZ, scale, viewRadiusM);
    if (u.trafficCars && u.trafficCars.length > 0) {
      this.drawTrafficCars(u.trafficCars, u.playerX, u.playerZ, scale);
    }
    if (u.orderPins && u.orderPins.length > 0) {
      this.drawOrderPins(u.orderPins, u.playerX, u.playerZ, scale);
    }

    ctx.restore();

    // Foreground (NOT rotated): player triangle in the center, compass needle if heading-up.
    this.drawPlayer(cx, cy, u.headingRad);
    if (this.mode === 'heading') {
      this.drawCompass(cx, cy, this.smoothedHeading);
    }
  }

  // --------- Drawing helpers ---------

  private drawTiles(playerX: number, playerZ: number, scale: number, viewRadiusM: number): void {
    if (!this.frame) return;
    const ctx = this.ctx;
    const tileZ = tileZoomFor(viewRadiusM);
    // Find tiles whose bbox intersects the visible square.
    // Conservative: take a slightly larger area to allow heading rotation overflow.
    const halfMeters = viewRadiusM * 1.5;
    const corners: Array<{ x: number; z: number }> = [
      { x: playerX - halfMeters, z: playerZ - halfMeters },
      { x: playerX + halfMeters, z: playerZ - halfMeters },
      { x: playerX - halfMeters, z: playerZ + halfMeters },
      { x: playerX + halfMeters, z: playerZ + halfMeters },
    ];
    let minTx = Infinity, maxTx = -Infinity, minTy = Infinity, maxTy = -Infinity;
    for (const c of corners) {
      const { lat, lng } = this.worldToLatLng(c.x, c.z);
      const tx = lngToTileFloat(lng, tileZ);
      const ty = latToTileFloat(lat, tileZ);
      if (tx < minTx) minTx = tx;
      if (tx > maxTx) maxTx = tx;
      if (ty < minTy) minTy = ty;
      if (ty > maxTy) maxTy = ty;
    }
    const tx0 = Math.floor(minTx);
    const tx1 = Math.floor(maxTx);
    const ty0 = Math.floor(minTy);
    const ty1 = Math.floor(maxTy);

    for (let tx = tx0; tx <= tx1; tx++) {
      for (let ty = ty0; ty <= ty1; ty++) {
        const img = this.getTile(tileZ, tx, ty);
        if (!img || !img.complete || img.naturalWidth === 0) continue;

        // Compute the tile's world-space bounds via its lat/lng corners.
        const nwLat = tileYToLat(ty, tileZ);
        const seLat = tileYToLat(ty + 1, tileZ);
        const nwLng = tileXToLng(tx, tileZ);
        const seLng = tileXToLng(tx + 1, tileZ);
        const nw = this.latLngToPlayerOffsetPx(nwLat, nwLng, playerX, playerZ, scale);
        const se = this.latLngToPlayerOffsetPx(seLat, seLng, playerX, playerZ, scale);

        // Tile pixel rectangle on the minimap. Note: latitudes decrease as ty increases,
        // so NW (low ty) has higher world Z → screen y is smaller (towards top).
        const px = nw.px;
        const py = nw.py;
        const w = se.px - nw.px;
        const h = se.py - nw.py;
        if (w <= 0 || h <= 0) continue;
        ctx.drawImage(img, px, py, w, h);
      }
    }

    // Slight dim overlay so the chunk borders pop.
    ctx.fillStyle = 'rgba(13, 17, 22, 0.18)';
    ctx.fillRect(0, 0, this.size, this.size);
  }

  private drawChunkOverlay(
    chunks: ChunkStateView[],
    playerX: number,
    playerZ: number,
    scale: number,
    viewRadiusM: number,
  ): void {
    if (!this.frame) return;
    const ctx = this.ctx;
    const chunkSize = this.frame.chunkSize;
    const half = chunkSize * 0.5;

    // Faint grid for visible chunks (whether requested or not).
    const visibleMeters = viewRadiusM * 1.4;
    const cxMin = Math.round((playerX - visibleMeters) / chunkSize);
    const cxMax = Math.round((playerX + visibleMeters) / chunkSize);
    const czMin = Math.round((playerZ - visibleMeters) / chunkSize);
    const czMax = Math.round((playerZ + visibleMeters) / chunkSize);

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    for (let cx = cxMin; cx <= cxMax; cx++) {
      for (let cz = czMin; cz <= czMax; cz++) {
        const center = chunkCenterWorld({ cx, cz }, chunkSize);
        const a = this.worldToPx(center.x - half, center.z - half, playerX, playerZ, scale);
        const b = this.worldToPx(center.x + half, center.z + half, playerX, playerZ, scale);
        ctx.strokeRect(a.px, a.py, b.px - a.px, b.py - a.py);
      }
    }

    // Known chunks on top with status-specific styling.
    for (const c of chunks) {
      const center = chunkCenterWorld(c.coord, chunkSize);
      const a = this.worldToPx(center.x - half, center.z - half, playerX, playerZ, scale);
      const b = this.worldToPx(center.x + half, center.z + half, playerX, playerZ, scale);
      const w = b.px - a.px;
      const h = b.py - a.py;
      switch (c.status) {
        case 'active':
          ctx.strokeStyle = 'rgba(80, 220, 120, 0.95)';
          ctx.lineWidth = 2;
          ctx.setLineDash([]);
          break;
        case 'cached':
          ctx.strokeStyle = 'rgba(180, 188, 200, 0.7)';
          ctx.lineWidth = 1;
          ctx.setLineDash([]);
          break;
        case 'pending':
          ctx.strokeStyle = 'rgba(255, 200, 60, 0.95)';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([4, 3]);
          break;
      }
      ctx.strokeRect(a.px, a.py, w, h);
    }
    ctx.setLineDash([]);
  }

  private drawTrafficCars(
    cars: Array<{ x: number; z: number }>,
    playerX: number,
    playerZ: number,
    scale: number,
  ): void {
    const ctx = this.ctx;
    ctx.save();
    // Amber fill with a dark outline so dots are readable over both light and dark tiles.
    ctx.fillStyle = '#ffc044';
    ctx.strokeStyle = 'rgba(13, 17, 22, 0.8)';
    ctx.lineWidth = 1;
    const DOT_RADIUS = 3;
    for (const car of cars) {
      const { px, py } = this.worldToPx(car.x, car.z, playerX, playerZ, scale);
      ctx.beginPath();
      ctx.arc(px, py, DOT_RADIUS, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();
  }

  private drawOrderPins(
    pins: OrderPinView[],
    playerX: number,
    playerZ: number,
    scale: number,
  ): void {
    if (!this.frame) return;
    const ctx = this.ctx;
    for (const pin of pins) {
      const { px, py } = this.latLngToPlayerOffsetPx(pin.lat, pin.lng, playerX, playerZ, scale);
      // Skip pins way off-canvas to save fill ops.
      if (px < -20 || py < -20 || px > this.size + 20 || py > this.size + 20) continue;

      const pulse = 0.4 + 0.6 * (Math.sin(pin.pulsePhase) + 1) * 0.5;
      const haloRadius = 10 + pulse * 4;
      const dotRadius = 5;

      const baseColor = pin.kind === 'pickup' ? (pin.premium ? '#ff8a3a' : '#ffc830') : '#5be58a';
      const dimAlpha = pin.claimedByOther ? 0.5 : 1.0;

      // Halo.
      ctx.save();
      ctx.globalAlpha = 0.25 * pulse * dimAlpha;
      ctx.fillStyle = baseColor;
      ctx.beginPath();
      ctx.arc(px, py, haloRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Dot.
      ctx.save();
      ctx.globalAlpha = dimAlpha;
      ctx.fillStyle = baseColor;
      ctx.strokeStyle = 'rgba(13, 17, 22, 0.85)';
      ctx.lineWidth = pin.premium ? 2.5 : 1.5;
      ctx.beginPath();
      ctx.arc(px, py, dotRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }
  }

  private drawPlayer(cx: number, cy: number, headingRad: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.translate(cx, cy);
    // In north-up mode the triangle rotates with heading; in heading-up the world has been
    // counter-rotated so the triangle always points "up" on screen.
    if (this.mode === 'north') {
      ctx.rotate(headingRad);
    }
    ctx.fillStyle = '#4aa9ff';
    ctx.strokeStyle = '#0d1116';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, -8);
    ctx.lineTo(6, 6);
    ctx.lineTo(0, 3);
    ctx.lineTo(-6, 6);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  /** Compass marker — small `N` near the edge pointing toward true north. */
  private drawCompass(cx: number, cy: number, smoothedHeading: number): void {
    const ctx = this.ctx;
    const radius = this.size * 0.5 - 18;
    // In heading-up mode the world is rotated by -heading, so screen-up is the heading
    // direction. True north relative to the player sits at angle (-heading) measured
    // clockwise from screen-up.
    const angle = -smoothedHeading;
    const x = cx + Math.sin(angle) * radius;
    const y = cy - Math.cos(angle) * radius;

    ctx.save();
    ctx.translate(x, y);
    ctx.fillStyle = 'rgba(13, 17, 22, 0.7)';
    ctx.beginPath();
    ctx.arc(0, 0, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ff6464';
    ctx.font = 'bold 12px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('N', 0, 0);
    ctx.restore();
  }

  // --------- Projection helpers ---------

  private worldToPx(
    wx: number,
    wz: number,
    playerX: number,
    playerZ: number,
    scale: number,
  ): { px: number; py: number } {
    const cx = this.size * 0.5;
    const cy = this.size * 0.5;
    return {
      px: cx + (wx - playerX) * scale,
      // Canvas Y grows downward; world Z grows north which we want to be screen-up.
      py: cy - (wz - playerZ) * scale,
    };
  }

  private latLngToPlayerOffsetPx(
    lat: number,
    lng: number,
    playerX: number,
    playerZ: number,
    scale: number,
  ): { px: number; py: number } {
    if (!this.frame) return { px: 0, py: 0 };
    const { x, z } = latLngToWorld({ lat, lng }, this.frame);
    return this.worldToPx(x, z, playerX, playerZ, scale);
  }

  private worldToLatLng(x: number, z: number): { lat: number; lng: number } {
    if (!this.frame) return { lat: 0, lng: 0 };
    // Inverse of latLngToWorld using the same equirectangular projection.
    const EARTH_RADIUS_M = 6_378_137;
    const meanLat = (this.frame.origin.lat * Math.PI) / 180;
    const lat = this.frame.origin.lat + ((z / EARTH_RADIUS_M) * 180) / Math.PI;
    const lng = this.frame.origin.lng + ((x / (EARTH_RADIUS_M * Math.cos(meanLat))) * 180) / Math.PI;
    return { lat, lng };
  }

  private getTile(z: number, x: number, y: number): HTMLImageElement | null {
    const key = `${z}/${x}/${y}`;
    let img = this.tileCache.get(key);
    if (img) return img;
    img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = TILE_URL(z, x, y);
    this.tileCache.set(key, img);
    return img;
  }
}

// --------- Slippy-map tile helpers ---------

function lngToTileFloat(lng: number, z: number): number {
  return ((lng + 180) / 360) * Math.pow(2, z);
}

function latToTileFloat(lat: number, z: number): number {
  const latRad = (lat * Math.PI) / 180;
  return (
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * Math.pow(2, z)
  );
}

function tileXToLng(tx: number, z: number): number {
  return (tx / Math.pow(2, z)) * 360 - 180;
}

function tileYToLat(ty: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * ty) / Math.pow(2, z);
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapAngle(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}
